import React, { useRef, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { RestrictedToAdmin, useAuthDataContext } from "../../../components/auth";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { DateTimePicker } from "@material-ui/pickers";
import InputAdornment from "@material-ui/core/InputAdornment";
import ChipInput from "material-ui-chip-input";
import SportApiForm from "../forms/common-forms/sport-api-form";
import EventLiveStreamApiForm from "../forms/common-forms/livestream-form/livestream-api-form";
import {
  LIVESTREAM_TYPE_TIIVII_OTT,
  STREAM_STATUS_DELETED,
} from "../forms/common-forms/livestream-form/livestream-form-constants";
import AddSubformButton from "../../../components/form/form-composed/parts/add-subform-button";
import EmailReceiptApiForm from "../forms/common-forms/email-receipt-form/email-receipt-api-form";
import FormCancel from "../../../components/form/form-cancel";
import FormSubmit from "../../../components/form/form-submit";
import { createEventService } from "../../../services/commonService";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import validationSchema from "../copy-event-button/validation-schema";
import { LOCAL_REC_EVENT } from "../forms/common-forms/basic-form";
import styles from "../copy-event-button/styles/copy-event-button.css";
import { withStyles } from "@material-ui/core/styles";
import { getEntityWithoutTypename } from "pages/utils";
import { getEventErrorMessage } from "pages/event/utils";
import DataGrid from "../../../components/data-grid/data-grid";
import DataGridItem from "../../../components/data-grid/data-grid-item";
import { dateFormatWithTime as defaultDateFormatWithTime } from "components/common";
import IconButton from "@material-ui/core/IconButton";
import DurationHumanizer from "../../../components/duration-humanizer/duration-humanizer";
import { PageHeaderBar } from "../../../components/page";
import PageSection from "../../../components/page/components/page-section/page-section";
import Icon from "@material-ui/core/Icon";
import cn from "classnames";
import { format } from "date-fns";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import { useLoadingContext } from "../../../components/loading-spinner-overlay";

const EventCopyForm = props => {
  const {
    intl,
    history,
    classes,
    event,
    customerId,
    contract,
    deployment,
    includeTestTemplates,
    handleSetIncludeTestTemplates,
    isLocalRecEvent,
    roomTemplates,
    handleSetIsLocalRecEvent,
    teamLogoAsset1,
    teamLogoAsset2,
  } = props;
  const { user } = useAuthDataContext();
  const { setLoading } = useLoadingContext();

  const [showSportDataConfig, setShowSportDataConfig] = useState(false);

  const handleGoBack = () => {
    history.push(`/event/details/${event._id}/customer/${customerId}`);
  };

  const mapLiveStreams = () => {
    return !!event?.livestreams?.length
      ? event.livestreams
          .filter(liveStream => liveStream.component_status !== STREAM_STATUS_DELETED)
          .map(liveStream => ({
            rtmpUrl: liveStream.rtmp_url || "",
            key: liveStream.key || "",
            publishingUrl: liveStream.publishing_url || "",
            description: liveStream.description || "",
            type: liveStream.type || "",
            componentName: liveStream.component_name || "",
            componentStatus: liveStream.component_status || "",
          }))
      : [];
  };

  const locales = {
    es_ES: "es",
    en_US: "en",
    gl_ES: "gl",
    ja_JP: "ja",
  };

  const emailReceipts = event?.email_receipts?.length
    ? event?.email_receipts?.map((email, index) => {
        const rawLocale = event?.email_locale[index];
        const locale = locales[rawLocale] || rawLocale || "es";
        return { email: email, locale };
      })
    : [{ email: user.email, locale: user.locale }];

  const liveStreams = mapLiveStreams();

  const initialLocalRecInfo = !!event?.local_rec_info?.user_emails?.length
    ? { userEmails: event?.local_rec_info?.user_emails }
    : null;

  const initialValues = getEntityWithoutTypename({
    title: event.event?.title,
    description: event.event?.description,
    liveStreams: liveStreams,
    startDate: null,
    endDate: null,
    visible: event.visible,
    billable: event.billable,
    isInternalTest: event.is_internal_test,
    activeQod: event.active_qod ?? false,
    isMutedOnStart: event.is_muted_on_start ?? false,
    unmuteMinutesBefore: event.unmute_minutes_before ?? 5,
    roomTemplateId: event.room_template_id,
    localRec: event.local_rec || false,
    localRecInfo: initialLocalRecInfo,
    emailReceipts: emailReceipts,
    sportEventData: {
      scoreboardType: event?.sport_event_data?.scoreboard_type,
      scoreboardTemplateId: event?.sport_event_data?.alt_scoreboard_template_id,
      teamLogo1: event?.sport_event_data?.team_logo_1 ?? "",
      teamLogo2: event?.sport_event_data?.team_logo_2 ?? "",
      teamLogoAsset1: teamLogoAsset1 ?? "",
      teamLogoAsset2: teamLogoAsset2 ?? "",
      teamName1: event?.sport_event_data?.team_name_1,
      teamName1Abreviated: event?.sport_event_data?.team_name_1_abreviated,
      teamName2: event?.sport_event_data?.team_name_2,
      teamName2Abreviated: event?.sport_event_data?.team_name_2_abreviated,
    },
    ...(event?.match_point_config && event?.match_point_config?.ocurrences_source !== null
      ? {
          matchPointConfig: {
            ocurrencesSourceId: event?.match_point_config?.ocurrences_source_id ?? "",
            ocurrencesVisible: event?.match_point_config?.ocurrences_visible,
            ocurrencesSource: event?.match_point_config?.ocurrences_source,
            matchPointUrl: event?.match_point_config?.match_point_url,
          },
        }
      : {}),
  });

  const copyEventCallback = r => {
    const newEvent = r;

    if (!!newEvent?._id) {
      setLoading(false);
      history.replace(`/event/details/${newEvent._id}/customer/${customerId}`);
    }
  };

  const [errorMessage, setErrorMessage] = useState("");

  const copyEventErrorCallback = e => {
    setLoading(false);
    const message = getEventErrorMessage(intl, e);
    setErrorMessage(message || intl.formatMessage({ id: "errors.event.create" }));
  };

  const formattedLocales = {
    es: "es_ES",
    en: "en_US",
    gl: "gl_ES",
    ja: "ja_JP",
  };

  const handleCopyEvent = values => {
    setLoading(true);
    const mapEmailLocales = () => {
      return values.emailReceipts?.map(email => formattedLocales[email.locale] ?? "es_ES");
    };

    const mapEmailReceipts = () => {
      return values.emailReceipts?.map(email => email.email);
    };

    const mapSportEventData = () => {
      const sportEventData = { ...values.sportEventData };

      return {
        team_name_1_abreviated: sportEventData.teamName1Abreviated || "",
        team_name_1: sportEventData.teamName1 || "",
        team_logo_1: sportEventData.teamLogo1 || "",
        team_name_2_abreviated: sportEventData.teamName2Abreviated || "",
        team_name_2: sportEventData.teamName2 || "",
        team_logo_2: sportEventData.teamLogo2 || "",
        alt_scoreboard_template_id: sportEventData.scoreboardTemplateId,
        scoreboard_type: sportEventData.scoreboardType,
      };
    };

    const formatLiveStreams = () => {
      return (
        values.liveStreams?.map(liveStream => {
          const isTiiviiOtt =
            liveStream.type === LIVESTREAM_TYPE_TIIVII_OTT ||
            liveStream.description === LIVESTREAM_TYPE_TIIVII_OTT;

          return {
            type: liveStream.type,
            description: liveStream.description,
            rtmp_url: isTiiviiOtt ? "" : liveStream.rtmpUrl,
            key: isTiiviiOtt ? "" : liveStream.key,
            publishing_url: isTiiviiOtt ? "" : liveStream.publishingUrl,
            component_name: "",
            component_status: "",
          };
        }) ?? []
      );
    };

    const body = {
      evu_id: event.evu_id,
      event_type_id: event.event_type_id,
      started_by_user_id: user.sub,
      keywords: event.keywords,
      service_contract_id: event.service_contract_id,
      url_mixer_bert: event.url_mixer_bert,
      elastic_ip: event.elastic_ip,
      room_template_id: values.roomTemplateId,
      event: {
        start_date: +format(values.startDate, "t"),
        end_date: +format(values.endDate, "t"),
        description: values.description,
        title: values.title,
      },
      livestreams: formatLiveStreams(),
      ...(event.is_sport_event && !isLocalRecEvent && { sport_event_data: mapSportEventData() }),
      billable: values.billable,
      visible: values.visible,
      is_internal_test: values.isInternalTest,
      is_muted_on_start: values.isMutedOnStart || false,
      unmute_minutes_before: values.isMutedOnStart ? Number(values.unmuteMinutesBefore ?? 0) : undefined,
      active_qod: !!(deployment?.allow_qod && values.activeQod),
      ...(deployment?.allow_qod && values.activeQod && deployment.msisdn_qod && { msisdn_qod: deployment.msisdn_qod }),
      email_receipts: mapEmailReceipts(),
      email_locale: mapEmailLocales(),
      ...(!!event.match_point_config?.generate_scoreboard_ocurrences && {
        match_point_config: event.match_point_config,
      }),
      remote_production_available: event.remote_production_available,
      local_rec: values.localRec || false,
      ...(isLocalRecEvent && {
        local_rec_info: { user_emails: values.localRecInfo.userEmails },
      }),
      authorized_recording: event.authorized_recording,
      ...(!!event.subdeployment && { subdeployment: event.subdeployment }),
      ...(!!event.graphic_set_id && { graphic_set_id: event.graphic_set_id }),
    };

    createEventService(body, copyEventCallback, copyEventErrorCallback);
  };

  const liveStreamsDefault = {
    rtmpUrl: "",
    key: "",
    description: "",
    publishingUrl: "",
    isNew: true,
  };

  const emailReceiptsDefault = { email: "", locale: "" };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    control,
  } = useForm({
    mode: "onChange",
    debug: true,
    resolver: yupResolver(
      validationSchema({
        intl,
        isLocalRecEvent,
        hasRoomTemplates: !!roomTemplates?.length,
        isSportEvent: event?.is_sport_event,
        showSportDataConfig,
        apiForm: true,
      }),
    ),
    values: initialValues,
  });

  const handleSetRoomTemplate = e => {
    setValue("roomTemplateId", e.target.value);
    const roomTemplate = roomTemplates.find(roomTemplate => roomTemplate._id === e.target.value);

    handleSetIsLocalRecEvent(roomTemplate?.type === LOCAL_REC_EVENT);
  };

  const handleChange = (field, value) => {
    setValue(field, value);
  };

  const values = watch();

  const { fields: emailReceiptsValues, append: appendEmail, remove: removeEmail } = useFieldArray({
    control,
    name: "emailReceipts",
  });

  const handleAddEmailReceipt = () => {
    appendEmail(emailReceiptsDefault);
  };

  const handleRemoveEmailReceipt = index => {
    removeEmail(index);
  };

  const { fields: liveStreamsReceiptsValues, append: appendLiveStream, remove: removeLiveStream } = useFieldArray({
    control,
    name: "liveStreams",
  });

  const handleAddLiveStream = () => {
    appendLiveStream(liveStreamsDefault);
  };

  const handleRemoveLiveStream = index => {
    removeLiveStream(index);
  };

  const { fields: localRecInfoUserEmailsValues, append: appendUserEmail, remove: removeUserEmail } = useFieldArray({
    control,
    name: isLocalRecEvent ? "localRecInfo.userEmails" : null,
  });

  const handleAddChip = chip => {
    appendUserEmail({ email: chip, locale: "es_ES" });
  };

  const handleDeleteChip = chip => {
    removeUserEmail(chip);
  };

  const dateTimePickerRef = useRef(null);

  const scrollToError = err => {
    if ((!!err.startDate || !!err.endDate) && dateTimePickerRef.current) {
      dateTimePickerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(handleCopyEvent, scrollToError)}>
        <DataGrid className={classes.flexColumn}>
          <PageSection className={cn(classes.fullWidth, classes.flexColumn)}>
            <DataGridItem big>
              <TextField
                {...register("title")}
                id="title"
                type="text"
                className={classes.fullWidth}
                placeholder={intl.formatMessage({ id: "common.title" })}
                label={intl.formatMessage({ id: "common.title" })}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </DataGridItem>
            <DataGridItem big>
              <TextField
                {...register("description")}
                id="description"
                type="text"
                placeholder={intl.formatMessage({ id: "common.description" })}
                label={intl.formatMessage({ id: "common.description" })}
                error={!!errors.description}
                helperText={errors.description?.message}
                className={classes.fullWidth}
              />
            </DataGridItem>
            <DataGridItem big>
              <FormControl className={classes.fullWidth}>
                <InputLabel id="room-template-select-label">{intl.formatMessage({ id: "event.template" })}</InputLabel>
                <Select
                  onChange={handleSetRoomTemplate}
                  value={watch("roomTemplateId")}
                  getLabelFromElement={item => item.codename}
                  getValueFromElement={item => item._id}
                  id="roomTemplateId"
                >
                  {roomTemplates?.map(roomTemplate => (
                    <MenuItem key={roomTemplate._id} value={roomTemplate._id}>
                      {roomTemplate.codename}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <RestrictedToAdmin>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeTestTemplates}
                      onChange={handleSetIncludeTestTemplates}
                      name="checkedB"
                      color="primary"
                    />
                  }
                  label={intl.formatMessage({ id: "app.page.event.edit.searchTestTemplates" })}
                />
              </RestrictedToAdmin>
            </DataGridItem>
            <RestrictedToAdmin>
              <DataGridItem big>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isInternalTest"
                      id="isInternalTest"
                      color="secondary"
                      checked={watch("isInternalTest")}
                      onChange={() => {
                        setValue("isInternalTest", !watch("isInternalTest"));
                        setValue("visible", false);
                        setValue("billable", false);
                      }}
                    />
                  }
                  label={intl.formatMessage({ id: "event.isInternalTest" })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register("visible")}
                      name="visible"
                      id="visible"
                      color="secondary"
                      checked={watch("visible")}
                      disabled={watch("isInternalTest")}
                    />
                  }
                  label={intl.formatMessage({ id: "event.visible" })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register("billable")}
                      name="billable"
                      id="billable"
                      color="secondary"
                      checked={watch("billable")}
                      disabled={watch("isInternalTest")}
                    />
                  }
                  label={intl.formatMessage({ id: "event.billable" })}
                />
                {!!contract?.local_rec_available && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register("localRec")}
                        name="localRec"
                        id="localRec"
                        color="secondary"
                        checked={watch("localRec")}
                      />
                    }
                    label={intl.formatMessage({ id: "event.local.rec" })}
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register("isMutedOnStart")}
                      name="isMutedOnStart"
                      id="isMutedOnStart"
                      color="secondary"
                      checked={!!watch("isMutedOnStart")}
                    />
                  }
                  label={intl.formatMessage({ id: "event.mutedOnStart" })}
                />
                {watch("isMutedOnStart") && (
                  <TextField
                    {...register("unmuteMinutesBefore")}
                    id="unmuteMinutesBefore"
                    type="number"
                    label={intl.formatMessage(
                      { id: "event.unmuteMinutesBefore" },
                      { minutes: Number(watch("unmuteMinutesBefore") ?? 0) },
                    )}
                    inputProps={{ min: 0, max: 20, step: 1 }}
                    InputLabelProps={{ shrink: true }}
                    style={{ minWidth: 220 }}
                    error={!!errors.unmuteMinutesBefore}
                    helperText={errors.unmuteMinutesBefore?.message}
                  />
                )}
              </DataGridItem>
            </RestrictedToAdmin>
            {!!deployment?.allow_qod && (
              <DataGridItem big>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...register("activeQod")}
                      name="activeQod"
                      id="activeQod"
                      color="secondary"
                      checked={!!watch("activeQod")}
                    />
                  }
                  label={intl.formatMessage({ id: "event.activeQod" })}
                />
              </DataGridItem>
            )}
            <div className={classes.flex}>
              <DataGridItem>
                <DateTimePicker
                  {...register("startDate")}
                  inputRef={dateTimePickerRef}
                  id="startDate"
                  ampm={false}
                  format={defaultDateFormatWithTime}
                  cancelLabel={intl.formatMessage({ id: "datepicker.cancel" })}
                  clearLabel={intl.formatMessage({ id: "datepicker.clear" })}
                  okLabel={intl.formatMessage({ id: "datepicker.ok" })}
                  todayLabel={intl.formatMessage({ id: "datepicker.today" })}
                  label={intl.formatMessage({ id: "common.since" })}
                  invalidDateMessage={intl.formatMessage({ id: "datepicker.date.notvalid" })}
                  margin="normal"
                  clearable
                  onAccept={value => {
                    const date = new Date(value._d);
                    const newDate = new Date(value._d).setHours(new Date(value._d).getHours() + 2);

                    setValue("startDate", date);
                    setValue("endDate", new Date(newDate));
                  }}
                  value={watch("startDate")}
                  allowKeyboardControl={false}
                  className={classes.fullWidth}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton>
                          <Icon name="date-range" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    className: classes.date_picker,
                  }}
                  error={!!errors.startDate}
                  helperText={errors.startDate?.message}
                />
                <DataGridItem big>
                  <DurationHumanizer start={watch("startDate")} end={watch("endDate")} />
                </DataGridItem>
              </DataGridItem>
              <DataGridItem>
                <DateTimePicker
                  {...register("endDate")}
                  inputRef={dateTimePickerRef}
                  id="endDate"
                  ampm={false}
                  format={defaultDateFormatWithTime}
                  cancelLabel={intl.formatMessage({ id: "datepicker.cancel" })}
                  clearLabel={intl.formatMessage({ id: "datepicker.clear" })}
                  okLabel={intl.formatMessage({ id: "datepicker.ok" })}
                  todayLabel={intl.formatMessage({ id: "datepicker.today" })}
                  label={intl.formatMessage({ id: "common.until" })}
                  invalidDateMessage={intl.formatMessage({ id: "datepicker.date.notvalid" })}
                  margin="normal"
                  clearable
                  onAccept={value => setValue("endDate", value._d)}
                  value={watch("endDate")}
                  allowKeyboardControl={false}
                  className={classes.fullWidth}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton>
                          <Icon name="date-range" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    className: classes.date_picker,
                  }}
                  error={!!errors.endDate}
                  helperText={errors.endDate?.message}
                />
              </DataGridItem>
            </div>
          </PageSection>
          {!!isLocalRecEvent && (
            <div>
              <PageHeaderBar subtitle title="event.local.rec.in.true" />
              <PageSection>
                <DataGrid>
                  <DataGridItem big>
                    <ChipInput
                      id="localRecInfo.userEmails"
                      value={localRecInfoUserEmailsValues.map(field => field?.email)}
                      onAdd={handleAddChip}
                      onDelete={handleDeleteChip}
                      fullWidth
                      label={intl.formatMessage({ id: "common.local_rec_emails" })}
                      helperText={
                        !!errors.localRecInfo?.userEmails
                          ? errors.localRecInfo?.userEmails.message
                          : intl.formatMessage({ id: "field.multipleEmails.help" })
                      }
                      classes={{}}
                      error={!!errors.localRecInfo?.userEmails}
                    />
                  </DataGridItem>
                </DataGrid>
              </PageSection>
            </div>
          )}
          {event?.is_sport_event && !isLocalRecEvent && (
            <PageSection className={cn(classes.fullWidth, classes.flexColumn)}>
              <SportApiForm
                eventToEdit={event}
                isNewForm={false}
                eventCustomerId={customerId}
                handleChange={handleChange}
                values={values}
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
                showSportDataConfig={showSportDataConfig}
                setShowSportDataConfig={setShowSportDataConfig}
              />
            </PageSection>
          )}
          {!isLocalRecEvent && (
            <DataGridItem big>
              <PageHeaderBar subtitle title="app.page.event.form.livestreams" />
              <PageSection>
                <DataGrid>
                  <div>
                    {liveStreamsReceiptsValues?.map((liveStream, index) => (
                      <EventLiveStreamApiForm
                        key={`liveStream-${index}`}
                        index={index}
                        handleChange={handleChange}
                        liveStream={liveStream}
                        eventCopy={event}
                        onDelete={() => handleRemoveLiveStream(index)}
                        classes={classes}
                        register={register}
                        errors={!!errors.liveStreams?.length ? errors.liveStreams[index] : {}}
                        setValue={setValue}
                        watch={watch}
                      />
                    ))}
                  </div>
                  <AddSubformButton
                    text={intl.formatMessage({ id: "app.page.event.form.add.emisionData" })}
                    onClick={handleAddLiveStream}
                  />
                </DataGrid>
              </PageSection>
            </DataGridItem>
          )}
          <DataGridItem big>
            <PageSection className={cn(classes.fullWidth)}>
              {emailReceiptsValues?.map((email, index) => (
                <EmailReceiptApiForm
                  key={`emailReceipt-${index}`}
                  register={register}
                  index={index}
                  email={email}
                  watch={watch}
                  onDelete={() => handleRemoveEmailReceipt(index)}
                  classes={classes}
                />
              ))}
              <AddSubformButton
                text={intl.formatMessage({ id: "app.page.event.form.add.emailReceipts" })}
                onClick={handleAddEmailReceipt}
              />
            </PageSection>
          </DataGridItem>
          <Snackbar
            open={!!errorMessage}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert onClose={() => setErrorMessage("")} severity="error">
              {errorMessage}
            </Alert>
          </Snackbar>
          <DataGridItem big>
            <div className={classes.button_container}>
              <FormCancel onClick={handleGoBack}>
                <FormattedMessage id="common.cancel" />
              </FormCancel>
              <FormSubmit disabled={isSubmitting}>
                <FormattedMessage id="common.accept" />
              </FormSubmit>
            </div>
          </DataGridItem>
        </DataGrid>
      </form>
    </div>
  );
};

export default injectIntl(withStyles(styles)(EventCopyForm));
